/**
 * @traction/events — catalogue UNIQUE des événements `tr:*` du thème (loi L5 / 16·R13).
 *
 * Toute constante d'événement vit ici ; les payloads sont typés en JSDoc.
 * Motif « intention → achèvement » (16·R14) : l'émetteur annonce l'intention
 * (`tr:cart-update`, `tr:variant-selected`…), SEUL le composant propriétaire
 * fait la requête puis annonce l'achèvement (`tr:cart-updated`). Un seul
 * mécanisme : `CustomEvent` sur `document` (survit au Section Rendering).
 *
 * NOTE migration : les émetteurs existants utilisent encore des chaînes
 * littérales — chaque vague migre SES modules vers ces constantes
 * (`import { TR_EVENTS } from './tr-events.js'` ou `window.Traction.events`).
 * `pubsub.js` (Dawn legacy : global.js, price-per-item.js, recipient-form.js)
 * est déprécié — à supprimer quand ses 3 consommateurs seront migrés (16·R17).
 */

/**
 * Un article à ajouter/ajouté au panier (contrat `getSelectedItems()`).
 * @typedef {object} TrCartItem
 * @property {number} id — id de variante.
 * @property {number} quantity
 * @property {Record<string, string>} [properties] — line item properties.
 */

/**
 * @typedef {object} TrCartAddDetail — payload de `tr:cart-add`.
 * @property {TrCartItem[]} items — articles ajoutés.
 * @property {object | null} cart — panier `/cart.js` après ajout (peut être null si refetch impossible).
 * @property {string} source — émetteur (`product-form`, `quick-add`, `bundle-fixed`…).
 */

/**
 * @typedef {object} TrCartUpdateDetail — payload de `tr:cart-update` (INTENTION — écouté par le moteur tr-cart).
 * @property {Record<string, number>} [updates] — `{ [lineKey]: quantity }` (mutation par key, jamais par index).
 * @property {string} [note]
 * @property {Record<string, string>} [attributes]
 * @property {string} [source]
 * @property {boolean} [render=true] — re-rendre les zones Section Rendering.
 */

/**
 * @typedef {object} TrCartUpdatedDetail — payload de `tr:cart-updated` (ACHÈVEMENT — émis par le seul moteur tr-cart).
 * @property {object} cart — panier `/cart.js` à jour.
 * @property {string} source
 * @property {Record<string, string>} sections — fragments HTML Section Rendering appliqués.
 */

/**
 * @typedef {object} TrCartErrorDetail — payload de `tr:cart-error`.
 * @property {string} message — message affichable (déjà localisé).
 * @property {number} status — statut HTTP (422 = relecture panier, 0 = réseau).
 * @property {string} source
 * @property {object | null} [cart]
 * @property {boolean} [engine] — true si émis par le moteur tr-cart.
 */

/**
 * @typedef {object} TrVariantChangeDetail — payload de `tr:variant-change` (ACHÈVEMENT — émis par tr-product-form).
 * @property {number | null} productId
 * @property {number} variantId
 * @property {object} variant — objet variante complet.
 * @property {boolean} available
 * @property {number} price — centimes.
 * @property {number | null} compareAtPrice — centimes.
 */

/**
 * @typedef {object} TrVariantSelectedDetail — payload de `tr:variant-selected` (INTENTION — émis par tr-variant-picker).
 * @property {object} variant — variante choisie par l'utilisateur.
 */

/**
 * @typedef {object} TrQuantityChangeDetail — payload de `tr:quantity-change` (tr-quantity-selector).
 * @property {number} quantity
 */

/**
 * @typedef {object} TrSelectionChangeDetail — payload de `tr:selection-change` (providers AOV : quantity-breaks, bundles, cross-sell).
 * @property {number} [quantity]
 * @property {number} [variantId]
 * @property {number} total — total affiché en centimes (prix calculés transportés par l'événement).
 */

/**
 * @typedef {object} TrFormChangeDetail — payload de `tr:form-change` (tr-product-form, non bublant).
 * @property {TrCartItem[]} items — sélection courante du formulaire.
 * @property {number} total — centimes.
 * @property {number} quantity
 * @property {boolean} multi — plusieurs lignes (bundle) ou une seule.
 */

/** Catalogue des noms d'événements — source unique, ne jamais redéclarer la chaîne ailleurs. */
export const TR_EVENTS = Object.freeze({
  /* Panier — intention (écoutés par le moteur tr-cart) */
  /** Demande d'ouverture du tiroir panier. Payload : aucun. */
  CART_OPEN: 'tr:cart-open',
  /** Demande de fermeture du tiroir panier. Payload : aucun. */
  CART_CLOSE: 'tr:cart-close',
  /** Demande de mutation du panier. @see TrCartUpdateDetail */
  CART_UPDATE: 'tr:cart-update',

  /* Panier — achèvement (émis par le moteur tr-cart / les formulaires) */
  /** Articles ajoutés au panier. @see TrCartAddDetail */
  CART_ADD: 'tr:cart-add',
  /** Panier re-rendu, état final. @see TrCartUpdatedDetail */
  CART_UPDATED: 'tr:cart-updated',
  /** Échec d'une opération panier. @see TrCartErrorDetail */
  CART_ERROR: 'tr:cart-error',
  /** Tiroir panier ouvert (émis par <tr-cart-drawer>). Payload : aucun. */
  CART_DRAWER_OPEN: 'tr:cart-drawer-open',
  /** Tiroir panier fermé (émis par <tr-cart-drawer>). Payload : aucun. */
  CART_DRAWER_CLOSE: 'tr:cart-drawer-close',

  /* Produit */
  /** Intention : l'utilisateur a choisi une variante. @see TrVariantSelectedDetail */
  VARIANT_SELECTED: 'tr:variant-selected',
  /** Achèvement : le formulaire a résolu la variante (prix/stock à jour). @see TrVariantChangeDetail */
  VARIANT_CHANGE: 'tr:variant-change',
  /** La quantité du sélecteur a changé. @see TrQuantityChangeDetail */
  QUANTITY_CHANGE: 'tr:quantity-change',
  /** Un provider AOV a changé la sélection (palier, bundle, cross-sell). @see TrSelectionChangeDetail */
  SELECTION_CHANGE: 'tr:selection-change',
  /** État consolidé du formulaire produit (items + total). @see TrFormChangeDetail */
  FORM_CHANGE: 'tr:form-change',

  /* Wishlist (12·R1→R6 — stockage localStorage versionné, jamais cookie) */
  /** État wishlist changé (ajout, retrait, sync multi-onglets). @see TrWishlistUpdatedDetail */
  WISHLIST_UPDATED: 'tr:wishlist-updated',
  /** Demande d'ouverture du tiroir wishlist. Payload : aucun. */
  WISHLIST_OPEN: 'tr:wishlist-open',
  /** Demande de fermeture du tiroir wishlist. Payload : aucun. */
  WISHLIST_CLOSE: 'tr:wishlist-close',

  /* Divers */
  /** Un compte à rebours est arrivé à zéro. Payload : aucun. */
  COUNTDOWN_END: 'tr:countdown-end',
  /** Notification globale (tr-toast). @see TrToastDetail */
  TOAST: 'tr:toast',
});

/**
 * @typedef {object} TrToastDetail — payload de `tr:toast`.
 * @property {string} message — texte déjà localisé (textContent, jamais du HTML).
 * @property {('success'|'error'|'info')} [type='info']
 * @property {number} [duration] — ms d'affichage (défaut 4000, erreurs 6000).
 */

/**
 * @typedef {object} TrWishlistUpdatedDetail — payload de `tr:wishlist-updated`.
 * @property {Array<{id:number, handle:string, addedAt:number}>} items — contenu à jour.
 * @property {number} count — nombre d'articles.
 * @property {string} source — émetteur (`button`, `drawer`, `storage`…).
 */

/* Pont non-ESM. */
window.Traction = window.Traction || {};
window.Traction.events = TR_EVENTS;

export default TR_EVENTS;
